import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Helmet } from "react-helmet";
import { toast } from "react-toastify";

import {
  getDiagnosticos,
  getDiagnosticoPdfUrl,
  getDiagnosticoInvestigacionUrl,
} from "../api/diagnosticos.api";
import useAuth from "../hooks/useAuth";
import { ESTADOS_DIAGNOSTICO } from "../utils/constants";
import { ENABLE_CLIENT_REPORTS } from "../utils/featureFlags";

import "../config.js";
import "./client-analysis.css";
import "./pagination.css";

const ClientAnalysis = (props) => {
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [companyLogoFailed, setCompanyLogoFailed] = useState(false);

  const [diagnosticos, setDiagnosticos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total_count: 0,
  });

  const [filters, setFilters] = useState({
    fecha_inicio: "",
    fecha_fin: "",
    fecha_actualizacion: "",
    estado: "",
  });

  const [downloadingId, setDownloadingId] = useState(null);

  const clientId = useMemo(() => {
    return (
      user?.cliente_id ??
      user?.clienteId ??
      user?.cliente?.id ??
      user?.id_cliente ??
      null
    );
  }, [user]);

  const investigacionHabilitada =
    user?.cliente?.investigacion_habilitada ?? false;

  const companyName = useMemo(() => {
    return (
      user?.nombre_empresa ??
      user?.empresa ??
      user?.company ??
      user?.cliente?.nombre_empresa ??
      "Tu empresa"
    );
  }, [user]);

  const companyLogoUrl = useMemo(() => {
    const raw =
      user?.cliente?.logo_url ?? user?.logo_url ?? user?.cliente_logo_url ?? "";

    if (!raw) return "";
    const value = String(raw).trim();
    if (!value) return "";

    if (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("data:") ||
      value.startsWith("blob:") ||
      value.startsWith("/api")
    ) {
      return value;
    }

    return value.startsWith("/") ? value : `/${value}`;
  }, [user]);

  const companyLogoSrc =
    !companyLogoFailed && companyLogoUrl
      ? companyLogoUrl
      : "/logo companies/logo-1-1500h.png";

  const isDateRangeInvalid =
    Boolean(filters.fecha_inicio) &&
    Boolean(filters.fecha_fin) &&
    filters.fecha_inicio > filters.fecha_fin;

  const getEstadoStyle = (estado) => {
    return (
      ESTADOS_DIAGNOSTICO[estado] || {
        label: estado,
        color: "#6B7280",
        bgColor: "#F3F4F6",
      }
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const [year, month, day] = dateString.split("T")[0].split("-");
    return `${day}/${month}/${year}`;
  };

  const fetchDiagnosticos = async () => {
    setLoading(true);
    setError(null);

    try {
      if (isDateRangeInvalid) {
        setDiagnosticos([]);
        setPagination((prev) => ({ ...prev, total_count: 0 }));
        setLoading(false);
        return;
      }

      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== ""),
      );

      if (clientId) {
        activeFilters.cliente_id = Number(clientId);
      }

      const response = await getDiagnosticos({
        ...activeFilters,
        page: pagination.page,
        limit: pagination.limit,
      });

      setDiagnosticos(response?.items ?? []);
      setPagination((prev) => ({
        ...prev,
        total_count: response?.total_count ?? 0,
      }));
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        "Error al cargar los diagnósticos";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      fecha_inicio: "",
      fecha_fin: "",
      fecha_actualizacion: "",
      estado: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
    setError(null);
  };

  const hasActiveFilters =
    filters.fecha_inicio !== "" ||
    filters.fecha_fin !== "" ||
    filters.fecha_actualizacion !== "" ||
    filters.estado !== "";

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleDownloadPdf = async (diagnosticoId) => {
    setDownloadingId(diagnosticoId);
    try {
      const response = await getDiagnosticoPdfUrl(diagnosticoId);
      if (response?.url) {
        window.open(response.url, "_blank");
      } else {
        toast.error("No se pudo obtener la URL del PDF");
      }
    } catch {
      toast.error("Error al descargar el análisis");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadInvestigacion = async (diagnosticoId) => {
    setDownloadingId(diagnosticoId);
    try {
      const response = await getDiagnosticoInvestigacionUrl(diagnosticoId);
      if (response?.url) {
        window.open(response.url, "_blank");
      } else {
        toast.error("No se pudo obtener la URL de la investigación");
      }
    } catch (err) {
      if (
        err?.response?.status === 403 &&
        err?.response?.data?.code === "INVESTIGACION_NOT_ENABLED"
      ) {
        toast.error("No tienes acceso a los informes de investigación");
      } else {
        toast.error("Error al descargar la investigación");
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const getTotalPages = () => {
    return Math.max(1, Math.ceil(pagination.total_count / pagination.limit));
  };

  const handlePageChange = (newPage) => {
    const totalPages = getTotalPages();
    if (newPage < 1 || newPage > totalPages) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  useEffect(() => {
    if (isDateRangeInvalid) {
      setError("La fecha de fin no puede ser anterior a la fecha de inicio");
      setLoading(false);
      setDiagnosticos([]);
      setPagination((prev) => ({ ...prev, total_count: 0 }));
      return;
    }

    fetchDiagnosticos();
  }, [
    pagination.page,
    filters.fecha_inicio,
    filters.fecha_fin,
    filters.estado,
    clientId,
    isDateRangeInvalid,
  ]);

  return (
    <div className="client-analysis-container">
      <Helmet>
        <title>BIOLAB</title>
        <meta property="og:title" content="client-analysis - BIOLAB Platform" />
        <link rel="canonical" href="https://${BASE_URL}/client-analysis" />
      </Helmet>
      <div className="window client-analysis-thq-window-elm">
        <header
          data-thq="thq-navbar"
          className="client-analysis-thq-navbar-elm"
        >
          <div
            data-thq="thq-navbar-nav"
            className="client-analysis-thq-desktop-menu-elm"
          >
            <div className="client-analysis-thq-top-elm1">
              <div className="client-analysis-thq-menus-elm1">
                <button
                  type="button"
                  className="client-analysis-thq-analysis-elm10 bodystandard"
                >
                  <img
                    alt="Análisis"
                    src="/menu icons/analysis.svg"
                    className="client-analysis-thq-icon-elm10"
                  />
                  <span className="client-analysis-thq-label-elm10 caption">
                    Análisis
                  </span>
                </button>
                {ENABLE_CLIENT_REPORTS && (
                  <Link
                    to="/client-report"
                    className="client-analysis-thq-reports-elm1 bodystandard"
                  >
                    <img
                      alt="Informes"
                      src="/menu icons/reports.svg"
                      className="client-analysis-thq-icon-elm11"
                    />
                    <span className="client-analysis-thq-label-elm11 caption">
                      Informes
                    </span>
                  </Link>
                )}
              </div>
            </div>
            <button
              type="button"
              className="client-analysis-thq-exit-elm1 bodystandard"
              onClick={logout}
            >
              <img
                alt="Salir"
                src="/menu icons/exit.svg"
                className="client-analysis-thq-icon-elm12"
              />
              <span className="client-analysis-thq-label-elm12 caption">
                Salir
              </span>
            </button>
          </div>

          <div
            data-thq="thq-burger-menu"
            className="client-analysis-thq-burger-menu-elm"
            onClick={() => setIsMobileMenuOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setIsMobileMenuOpen(true);
            }}
          >
            <svg viewBox="0 0 1024 1024" className="client-analysis-icon1">
              <path d="M128 554.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 298.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 810.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667z"></path>
            </svg>
          </div>

          <div
            data-thq="thq-mobile-menu"
            className={`client-analysis-thq-mobile-menu-elm${
              isMobileMenuOpen ? " client-analysis-thq-mobile-menu-open" : ""
            }`}
          >
            <div className="client-analysis-thq-nav-elm">
              <div className="client-analysis-thq-content-elm1">
                <div className="client-analysis-thq-header-elm1">
                  <div className="client-analysis-thq-logos-elm1">
                    <img
                      alt="BIOLAB Logo"
                      src="/biolab-logo-200h.png"
                      className="client-analysis-thq-biolablogo-elm1"
                    />
                    <img
                      alt={companyName}
                      src={companyLogoSrc}
                      className="client-analysis-thq-company-logo-elm1"
                      onError={() => setCompanyLogoFailed(true)}
                    />
                  </div>
                  <div
                    data-thq="thq-close-menu"
                    className="client-analysis-thq-close-menu-elm"
                    onClick={closeMobileMenu}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") closeMobileMenu();
                    }}
                  >
                    <img
                      alt="image"
                      src="/close.svg"
                      className="client-analysis-thq-icon-elm13"
                    />
                  </div>
                </div>
                <div className="client-analysis-thq-top-elm2">
                  <div className="client-analysis-thq-menus-elm2">
                    <button
                      type="button"
                      className="client-analysis-thq-analysis-elm11 bodystandard"
                    >
                      <img
                        alt="Análisis"
                        src="/menu icons/analysis.svg"
                        className="client-analysis-thq-icon-elm14"
                      />
                      <span className="client-analysis-thq-label-elm13">
                        Análisis
                      </span>
                    </button>
                    {ENABLE_CLIENT_REPORTS && (
                      <Link
                        to="/client-report"
                        className="client-analysis-thq-reports-elm2 bodystandard"
                        onClick={closeMobileMenu}
                      >
                        <img
                          alt="Informes"
                          src="/menu icons/reports.svg"
                          className="client-analysis-thq-icon-elm15"
                        />
                        <span className="client-analysis-thq-label-elm14">
                          Informes
                        </span>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="client-analysis-thq-exit-elm2 bodystandard"
                onClick={logout}
              >
                <img
                  alt="Salir"
                  src="/menu icons/exit.svg"
                  className="client-analysis-thq-icon-elm16"
                />
                <span className="client-analysis-thq-label-elm15">Salir</span>
              </button>
            </div>
          </div>
        </header>

        <div className="client-analysis-thq-content-elm2">
          <div className="client-analysis-thq-title-elm1">
            <h1 className="client-analysis-thq-title-elm2">Análisis</h1>
            <div className="client-analysis-thq-logos-elm2">
              <img
                alt="BIOLAB Logo"
                src="/biolab-logo-200h.png"
                className="client-analysis-thq-biolablogo-elm2"
              />
              <img
                alt={companyName}
                src={companyLogoSrc}
                className="client-analysis-thq-company-logo-elm2"
                onError={() => setCompanyLogoFailed(true)}
              />
            </div>
          </div>

          <div className="client-analysis-thq-content-elm3">
            <div className="client-analysis-modern-content">
              <div className="client-analysis-modern-filters">
                <label className="client-analysis-filter-pill">
                  <span className="client-analysis-filter-label bodysmall">
                    Fecha de inicio
                  </span>
                  <div className="client-analysis-filter-input-row">
                    <img
                      alt=""
                      src="/date.svg"
                      className="client-analysis-filter-icon"
                      aria-hidden="true"
                    />
                    <input
                      type="date"
                      value={filters.fecha_inicio}
                      max={filters.fecha_fin || undefined}
                      onChange={(e) =>
                        handleFilterChange("fecha_inicio", e.target.value)
                      }
                      className="client-analysis-filter-native bodysmall"
                    />
                    <svg
                      className="client-analysis-filter-chevron"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </label>

                <label className="client-analysis-filter-pill">
                  <span className="client-analysis-filter-label bodysmall">
                    Fecha de fin
                  </span>
                  <div className="client-analysis-filter-input-row">
                    <img
                      alt=""
                      src="/date.svg"
                      className="client-analysis-filter-icon"
                      aria-hidden="true"
                    />
                    <input
                      type="date"
                      value={filters.fecha_fin}
                      min={filters.fecha_inicio || undefined}
                      onChange={(e) =>
                        handleFilterChange("fecha_fin", e.target.value)
                      }
                      className="client-analysis-filter-native bodysmall"
                    />
                    <svg
                      className="client-analysis-filter-chevron"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </label>

                <label className="client-analysis-filter-pill">
                  <span className="client-analysis-filter-label bodysmall">
                    Fecha de actualización
                  </span>
                  <div className="client-analysis-filter-input-row">
                    <img
                      alt=""
                      src="/date.svg"
                      className="client-analysis-filter-icon"
                      aria-hidden="true"
                    />
                    <input
                      type="date"
                      value={filters.fecha_actualizacion}
                      onChange={(e) =>
                        handleFilterChange(
                          "fecha_actualizacion",
                          e.target.value,
                        )
                      }
                      className="client-analysis-filter-native bodysmall"
                    />
                    <svg
                      className="client-analysis-filter-chevron"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </label>

                <label className="client-analysis-filter-pill">
                  <span className="client-analysis-filter-label bodysmall">
                    Estado
                  </span>
                  <div className="client-analysis-filter-input-row">
                    <img
                      alt=""
                      src="/status.svg"
                      className="client-analysis-filter-icon"
                      aria-hidden="true"
                    />
                    <select
                      value={filters.estado}
                      onChange={(e) =>
                        handleFilterChange("estado", e.target.value)
                      }
                      className="client-analysis-filter-native bodysmall"
                    >
                      <option value="">Todos los estados</option>
                      {Object.entries(ESTADOS_DIAGNOSTICO).map(
                        ([key, { label }]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ),
                      )}
                    </select>
                    <svg
                      className="client-analysis-filter-chevron"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </label>
              </div>

              {(hasActiveFilters || isDateRangeInvalid) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="client-analysis-clear-filters bodysmall"
                >
                  Limpiar filtros
                </button>
              )}

              {isDateRangeInvalid && (
                <div className="client-analysis-filter-error bodysmall">
                  La fecha de fin no puede ser anterior a la fecha de inicio.
                </div>
              )}
            </div>

            <div
              className="client-analysis-thq-table-elm"
              style={{
                "--table-cols": investigacionHabilitada
                  ? "80px 1fr 1fr 1fr 1.4fr 140px 140px 140px"
                  : "80px 1fr 1fr 1fr 1.4fr 140px 140px",
              }}
            >
              <div className="client-analysis-thq-header-elm2">
                <span className="client-analysis-thq-text-elm105">ID</span>
                <span className="client-analysis-thq-text-elm106">
                  Purchase Order
                </span>
                <span className="client-analysis-thq-text-elm106">
                  Fecha inicio
                </span>
                <span className="client-analysis-thq-text-elm107">
                  Fecha fin
                </span>
                <span className="client-analysis-thq-text-elm108">
                  Fecha de actualización
                </span>
                <span className="client-analysis-thq-text-elm109">Estado</span>
                <span className="client-analysis-thq-text-elm110">
                  Análisis
                </span>
                {investigacionHabilitada && (
                  <span className="client-analysis-thq-text-elm110">
                    Investigación
                  </span>
                )}
              </div>

              {loading && (
                <div className="client-analysis-thq-row1-elm1">
                  <span className="client-analysis-thq-text-elm111 bodysmall">
                    Cargando...
                  </span>
                </div>
              )}

              {!loading && error && (
                <div className="client-analysis-thq-row1-elm1">
                  <span className="client-analysis-thq-text-elm111 bodysmall">
                    {error}
                  </span>
                </div>
              )}

              {!loading && !error && diagnosticos.length === 0 && (
                <div className="client-analysis-thq-row1-elm1">
                  <span className="client-analysis-thq-text-elm111 bodysmall">
                    No hay diagnósticos
                  </span>
                </div>
              )}

              {!loading &&
                !error &&
                diagnosticos.map((diag) => {
                  const estadoStyle = getEstadoStyle(diag.estado);

                  return (
                    <div
                      key={diag.identificador_diagnostico}
                      className="client-analysis-thq-row1-elm1"
                    >
                      <span className="client-analysis-thq-text-elm111 bodysmall">
                        {diag.identificador_diagnostico}
                      </span>
                      <span className="client-analysis-thq-text-elm111 bodysmall">
                        {diag.purchase_order || "—"}
                      </span>
                      <span className="client-analysis-thq-text-elm112 bodysmall">
                        {formatDate(diag.fecha_inicio)}
                      </span>
                      <span className="client-analysis-thq-text-elm113 bodysmall">
                        {formatDate(diag.fecha_fin)}
                      </span>
                      <span className="client-analysis-thq-text-elm114 bodysmall">
                        {formatDate(diag.fecha_actualizacion)}
                      </span>
                      <div className="client-analysis-thq-status-elm10">
                        <div
                          className="client-analysis-thq-complete-elm12"
                          style={{ backgroundColor: estadoStyle.bgColor }}
                        >
                          <span
                            className="client-analysis-thq-text-elm115"
                            style={{ color: estadoStyle.color }}
                          >
                            {estadoStyle.label}
                          </span>
                        </div>
                      </div>
                      <div className="client-analysis-thq-download-elm10">
                        <button
                          type="button"
                          onClick={() =>
                            diag.documento_pdf_url && handleDownloadPdf(diag.id)
                          }
                          disabled={
                            downloadingId === diag.id || !diag.documento_pdf_url
                          }
                          className="client-analysis-download-link"
                          style={{
                            opacity: !diag.documento_pdf_url ? 0.35 : 1,
                          }}
                        >
                          <img
                            alt="Descargar"
                            src="/download.svg"
                            className="client-analysis-thq-icon-elm22"
                          />
                          <span className="bodysmall">Descargar</span>
                        </button>
                      </div>
                      {investigacionHabilitada && (
                        <div className="client-analysis-thq-download-elm10">
                          {diag.tiene_investigacion ? (
                            <button
                              type="button"
                              onClick={() =>
                                handleDownloadInvestigacion(diag.id)
                              }
                              disabled={downloadingId === diag.id}
                              className="client-analysis-download-link"
                            >
                              <img
                                alt="Descargar investigación"
                                src="/download.svg"
                                className="client-analysis-thq-icon-elm22"
                              />
                              <span className="bodysmall">Descargar</span>
                            </button>
                          ) : (
                            <span
                              className="bodysmall"
                              style={{ opacity: 0.35 }}
                            >
                              —
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            <div className="client-analysis-thq-table-mobile-elm">
              {loading && (
                <div className="client-analysis-mobile-card">
                  <span className="bodysmall">Cargando...</span>
                </div>
              )}

              {!loading && error && (
                <div className="client-analysis-mobile-card">
                  <span className="bodysmall">{error}</span>
                </div>
              )}

              {!loading && !error && diagnosticos.length === 0 && (
                <div className="client-analysis-mobile-card">
                  <span className="bodysmall">No hay diagnósticos</span>
                </div>
              )}

              {!loading &&
                !error &&
                diagnosticos.map((diag) => {
                  const estadoStyle = getEstadoStyle(diag.estado);

                  return (
                    <div
                      key={`mobile-${diag.identificador_diagnostico}`}
                      className="client-analysis-mobile-card"
                    >
                      <div className="client-analysis-mobile-card-header">
                        <span className="client-analysis-mobile-card-title">
                          {diag.identificador_diagnostico}
                        </span>
                        <div
                          className="client-analysis-thq-complete-elm12"
                          style={{ backgroundColor: estadoStyle.bgColor }}
                        >
                          <span
                            className="client-analysis-thq-text-elm115"
                            style={{ color: estadoStyle.color }}
                          >
                            {estadoStyle.label}
                          </span>
                        </div>
                      </div>

                      <div className="client-analysis-mobile-grid">
                        <div>
                          <span className="caption">Purchase Order</span>
                          <span className="bodysmall">
                            {diag.purchase_order || "—"}
                          </span>
                        </div>
                        <div>
                          <span className="caption">Fecha inicio</span>
                          <span className="bodysmall">
                            {formatDate(diag.fecha_inicio)}
                          </span>
                        </div>
                        <div>
                          <span className="caption">Fecha fin</span>
                          <span className="bodysmall">
                            {formatDate(diag.fecha_fin)}
                          </span>
                        </div>
                        <div>
                          <span className="caption">Actualización</span>
                          <span className="bodysmall">
                            {formatDate(diag.fecha_actualizacion)}
                          </span>
                        </div>
                        {investigacionHabilitada &&
                          diag.tiene_investigacion && (
                            <div style={{ gridColumn: "1 / -1" }}>
                              <span className="caption">Investigación</span>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDownloadInvestigacion(diag.id)
                                }
                                disabled={downloadingId === diag.id}
                                className="client-analysis-mobile-download-button"
                              >
                                <img
                                  alt="Descargar investigación"
                                  src="/download.svg"
                                />
                                <span>Descargar investigación</span>
                              </button>
                            </div>
                          )}
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          diag.documento_pdf_url && handleDownloadPdf(diag.id)
                        }
                        disabled={
                          downloadingId === diag.id || !diag.documento_pdf_url
                        }
                        className="client-analysis-mobile-download-button"
                        style={{ opacity: !diag.documento_pdf_url ? 0.35 : 1 }}
                      >
                        <img alt="Descargar" src="/download.svg" />
                        <span>
                          {downloadingId === diag.id
                            ? "Descargando..."
                            : diag.documento_pdf_url
                              ? "Descargar análisis"
                              : "Análisis no disponible"}
                        </span>
                      </button>
                    </div>
                  );
                })}
            </div>

            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Anterior
              </button>
              <span>
                Página {pagination.page} de {getTotalPages()}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= getTotalPages()}
              >
                Siguiente
              </button>
              <span>Total: {pagination.total_count} diagnósticos</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientAnalysis;
